import React, { useState } from 'react';

const DataField = ({ label, value }) => (
  <div className="flex flex-col gap-1">
    <div className="text-sm text-gray-400 font-medium">{label}</div>
    <div className="text-white font-medium break-words">
      {value || 'Not specified'}
    </div>
  </div>
);

const ListingOverview = ({ listing, onSpecializedDataUpdate, actionLoading }) => {
  const [activeTab, setActiveTab] = useState('details');
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(amount || 0);
  };

  const renderSpecializedData = () => {
    if (!listing?.specialized_data) {
      return (
        <div className="text-center py-8 group">
          <div className="text-4xl mb-4 text-gray-600 group-hover:text-[#ff660e] transition-colors">📦</div>
          <p className="text-gray-400">No specialized data available</p>
        </div>
      );
    }

    const { _type, ...data } = listing.specialized_data;

    const renderers = {
      electronics: () => (
        <div className="space-y-4">
          <h4 className="font-bold text-white text-lg border-b border-gray-700 pb-2">Electronics Details</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.brand && <DataField label="Brand" value={data.brand} />}
            {data.model && <DataField label="Model" value={data.model} />}
            {data.storage_capacity && <DataField label="Storage" value={data.storage_capacity} />}
            {data.ram && <DataField label="RAM" value={data.ram} />}
            {data.screen_size && <DataField label="Screen Size" value={data.screen_size} />}
            {data.screen_resolution && <DataField label="Screen Resolution" value={data.screen_resolution} />}
            {data.processor && <DataField label="Processor" value={data.processor} />}
            {data.graphics_card && <DataField label="Graphics Card" value={data.graphics_card} />}
            {data.color && <DataField label="Color" value={data.color} />}
            {data.battery_health && <DataField label="Battery Health" value={data.battery_health} />}
            {data.network_type && <DataField label="Network Type" value={data.network_type} />}
            {data.cellular_support && <DataField label="Cellular Support" value={data.cellular_support} />}
            {data.operating_system && <DataField label="Operating System" value={data.operating_system} />}
            {data.includes_accessories && <DataField label="Includes Accessories" value={data.includes_accessories ? 'Yes' : 'No'} />}
            {data.features && <DataField label="Features" value={data.features} />}
            {data.compatibility && <DataField label="Compatibility" value={data.compatibility} />}
            {data.speaker_power && <DataField label="Speaker Power" value={data.speaker_power} />}
            {data.connectivity && <DataField label="Connectivity" value={data.connectivity} />}
            {data.sensor_size && <DataField label="Sensor Size" value={data.sensor_size} />}
            {data.megapixels && <DataField label="Megapixels" value={data.megapixels} />}
            {data.len_type && <DataField label="Lens Type" value={data.len_type} />}
            {data.shutter_count && <DataField label="Shutter Count" value={data.shutter_count} />}
            {data.edition && <DataField label="Edition" value={data.edition} />}
            {data.includes_games && <DataField label="Includes Games" value={data.includes_games ? 'Yes' : 'No'} />}
          </div>
        </div>
      ),
      
      vehicles: () => (
        <div className="space-y-4">
          <h4 className="font-bold text-white text-lg border-b border-gray-700 pb-2">Vehicle Details</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.condition && <DataField label="Condition" value={data.condition} />}
            {data.make && <DataField label="Make" value={data.make} />}
            {data.model && <DataField label="Model" value={data.model} />}
            {data.vehicle_type && <DataField label="Vehicle Type" value={data.vehicle_type} />}
            {data.boat_type && <DataField label="Boat Type" value={data.boat_type} />}
            {data.engine_cc && <DataField label="Engine CC" value={`${data.engine_cc}cc`} />}
            {data.length && <DataField label="Length" value={data.length} />}
            {data.engine_type && <DataField label="Engine Type" value={data.engine_type} />}
            {data.hull_material && <DataField label="Hull Material" value={data.hull_material} />}
            {data.truck_type && <DataField label="Truck Type" value={data.truck_type} />}
            {data.load_capacity && <DataField label="Load Capacity" value={data.load_capacity} />}
            {data.year && <DataField label="Year" value={data.year} />}
            {data.trim && <DataField label="Trim" value={data.trim} />}
            {data.body_type && <DataField label="Body Type" value={data.body_type} />}
            {data.transmission && <DataField label="Transmission" value={data.transmission} />}
            {data.fuel_type && <DataField label="Fuel Type" value={data.fuel_type} />}
            {data.drivetrain && <DataField label="Drivetrain" value={data.drivetrain} />}
            {data.engine_size && <DataField label="Engine Size" value={`${data.engine_size}L`} />}
            {data.cylinders && <DataField label="Cylinders" value={data.cylinders} />}
            {data.horsepower && <DataField label="Horsepower" value={`${data.horsepower} HP`} />}
            {data.color && <DataField label="Color" value={data.color} />}
            {data.interior_color && <DataField label="Interior Color" value={data.interior_color} />}
            {data.seats && <DataField label="Seats" value={data.seats} />}
            {data.doors && <DataField label="Doors" value={data.doors} />}
            {data.mileage && <DataField label="Mileage" value={`${data.mileage.toLocaleString()} km`} />}
            {data.is_registered !== undefined && <DataField label="Registered" value={data.is_registered ? 'Yes' : 'No'} />}
            {data.custom_duty_paid !== undefined && <DataField label="Custom Duty Paid" value={data.custom_duty_paid ? 'Yes' : 'No'} />}
            {data.price_negotiable !== undefined && <DataField label="Price Negotiable" value={data.price_negotiable ? 'Yes' : 'No'} />}
          </div>
        </div>
      ),
      
      properties: () => (
        <div className="space-y-4">
          <h4 className="font-bold text-white text-lg border-b border-gray-700 pb-2">Property Details</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.property_type && <DataField label="Type" value={data.property_type} />}
            {data.bedrooms && <DataField label="Bedrooms" value={data.bedrooms} />}
            {data.bathrooms && <DataField label="Bathrooms" value={data.bathrooms} />}
            {data.toilets && <DataField label="Toilets" value={data.toilets} />}
            {data.area_size && <DataField label="Area" value={`${data.area_size} ${data.area_unit}`} />}
            {data.furnishing && <DataField label="Furnishing" value={data.furnishing} />}
            {data.parking_spaces && <DataField label="Parking" value={data.parking_spaces} />}
            {data.floor_number && <DataField label="Floor Number" value={data.floor_number} />}
            {data.total_floors && <DataField label="Total Floors" value={data.total_floors} />}
            {data.property_status && <DataField label="Property Status" value={data.property_status} />}
            {data.service_charge && <DataField label="Service Charge" value={data.service_charge} />}
            {data.topography && <DataField label="Topography" value={data.topography} />}
          </div>
        </div>
      ),
      
      services: () => (
        <div className="space-y-4">
          <h4 className="font-bold text-white text-lg border-b border-gray-700 pb-2">Service Details</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.service_type && <DataField label="Service Type" value={data.service_type} />}
            {data.specialization && <DataField label="Specialization" value={data.specialization} />}
            {data.cleaning_type && <DataField label="Cleaning Type" value={data.cleaning_type} />}
            {data.team_size && <DataField label="Team Size" value={data.team_size} />}
            {data.cleaning_duration && <DataField label="Cleaning Duration" value={data.cleaning_duration} />}
            {data.service_focus && <DataField label="Service Focus" value={data.service_focus} />}
            {data.session_type && <DataField label="Session Type" value={data.session_type} />}
            {data.experience_years && <DataField label="Experience" value={`${data.experience_years} years`} />}
            {data.qualification && <DataField label="Qualification" value={data.qualification} />}
            {data.service_area && <DataField label="Service Area" value={data.service_area} />}
            {data.availability && <DataField label="Availability" value={data.availability} />}
            {data.pricing_type && <DataField label="Pricing Type" value={data.pricing_type} />}
            {data.hourly_rate && <DataField label="Hourly Rate" value={data.hourly_rate} />}
            {data.fixed_price && <DataField label="Fixed Price" value={data.fixed_price} />}
            {data.subject && <DataField label="Subject" value={data.subject} />}
            {data.educational_level && <DataField label="Educational Level" value={data.educational_level} />}
          </div>
        </div>
      )
    };

    const renderer = renderers[_type] || (() => (
      <div className="text-center py-8 group">
        <div className="text-4xl mb-4 text-gray-600 group-hover:text-[#ff660e] transition-colors">📦</div>
        <h4 className="font-semibold text-gray-300 mb-2">General Listing</h4>
        <p className="text-gray-500 text-sm">No specialized data available.</p>
      </div>
    ));
    
    return renderer();
  };

  const renderBasicDetails = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl p-6 border border-gray-700">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="text-sm text-gray-400 mb-1">Price</div>
            <div className="text-3xl font-bold text-[#ff660e]">
              {formatCurrency(listing?.price)}
            </div>
            {listing?.is_negotiable && (
              <div className="inline-flex items-center gap-1 mt-2 px-3 py-1 rounded-full bg-[#ff660e]/20 text-[#ff660e] text-sm font-medium border border-[#ff660e]/30">
                <span>🤝</span>
                <span>Price Negotiable</span>
              </div>
            )}
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-400 mb-1">Category</div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-700 rounded-xl border border-gray-600 group">
              <span className="text-lg text-gray-300 group-hover:text-[#ff660e] transition-colors">📦</span>
              <span className="font-semibold text-white">{listing?.category_name}</span>
            </div>
          </div>
        </div>
      </div>

      <div>
        <h4 className="font-bold text-white text-lg mb-3">Description</h4>
        <div className="prose max-w-none text-gray-300 bg-gray-800 rounded-xl p-5 border border-gray-700">
          {listing?.description || 'No description provided.'}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <h4 className="font-bold text-white text-lg">Listing Information</h4>
          <div className="space-y-3 bg-gray-800 rounded-xl p-4 border border-gray-700">
            <DataField label="Listing Type" value={listing?.listing_type} />
            <DataField label="Condition" value={listing?.condition} />
            <DataField label="Created" value={listing?.created_at ? new Date(listing.created_at).toLocaleDateString() : 'N/A'} />
            <DataField label="Last Updated" value={listing?.updated_at ? new Date(listing.updated_at).toLocaleDateString() : 'N/A'} />
          </div>
        </div>

        <div className="space-y-4">
          <h4 className="font-bold text-white text-lg">Location</h4>
          <div className="space-y-3 bg-gray-800 rounded-xl p-4 border border-gray-700">
            <DataField label="State" value={listing?.state_name} />
            <DataField label="LGA" value={listing?.lga_name} />
            <DataField label="LCDA" value={listing?.lcda_name} />
          </div>
        </div>
      </div>
    </div>
  );

  const renderAdminNotes = () => (
    <div className="space-y-4">
      <h4 className="font-bold text-white text-lg">Admin Notes</h4>
      <div className="bg-yellow-900/20 border border-yellow-800/50 rounded-xl p-4">
        <div className="font-semibold text-yellow-300 mb-1">Moderator Notes</div>
        <div className="text-gray-300">
          {listing?.admin_notes || 'No admin notes available.'}
        </div>
      </div>

      {listing?.moderated_by && (
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DataField label="Moderated By" value={`Admin #${listing.moderated_by}`} />
            <DataField label="Moderated At" value={listing.moderated_at ? new Date(listing.moderated_at).toLocaleString() : 'N/A'} />
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-gray-800 rounded-2xl shadow-lg border border-gray-700 overflow-hidden backdrop-blur-sm">
      <div className="border-b border-gray-700">
        <div className="flex">
          {['details', 'specialized', 'admin'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 px-6 py-4 font-medium text-sm transition-all duration-200 hover:bg-gray-750 ${
                activeTab === tab 
                  ? 'text-white' 
                  : 'text-gray-400 hover:text-gray-300'
              }`}
              style={{
                borderBottom: activeTab === tab ? `2px solid #ff660e` : 'none'
              }}
            >
              {tab === 'details' && '📋 Basic Details'}
              {tab === 'specialized' && '🎯 Specialized Data'}
              {tab === 'admin' && '👮 Admin Notes'}
            </button>
          ))}
        </div>
      </div>

      <div className="p-6">
        {activeTab === 'details' && renderBasicDetails()}
        {activeTab === 'specialized' && renderSpecializedData()}
        {activeTab === 'admin' && renderAdminNotes()}
      </div>
    </div>
  );
};

export default ListingOverview;